/* Version: 3.5.149 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useCallback, createElement } from 'react';
import PropTypes from 'prop-types';
import { HEADER_VARIANT } from '../../UtilityHeader/constants.js';
import { BREAKPOINTS } from '../../../spacing/index.js';
import { safeRest, noop } from '../../../utils/functions.js';
import { getDefaultTab } from '../../../utils/getDefaultTab.js';
import handle from '../../../utils/handle.js';
import { getTabProps } from '../utils.js';
import HeaderButton from './HeaderButton.js';
import MenuItem from './MenuItem.js';
import { TabList, TabItem, TabComponent } from './styledComponents.js';
import { jsx } from 'react/jsx-runtime';
import useTheme from '../../../hooks/useTheme.js';
import useDerivedState from '../../../hooks/useDerivedState.js';
import useMedia from '../../../hooks/useMedia.js';

var _excluded = ["as", "id", "label", "value", "to", "isExternal"],
  _excluded2 = ["as", "id", "label", "value", "to", "isExternal"];
var HeaderTabs = function HeaderTabs(_ref) {
  var _tabs$find;
  var _ref$tabs = _ref.tabs,
    tabs = _ref$tabs === undefined ? [] : _ref$tabs,
    defaultSelectedTab = _ref.selectedTab,
    controlledSelectedTab = _ref.controlledSelectedTab,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === undefined ? noop : _ref$onChange,
    variant = _ref.variant;
  var theme = useTheme();
  var _useDerivedState = useDerivedState(getDefaultTab(tabs, defaultSelectedTab), controlledSelectedTab),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 2),
    selectedTab = _useDerivedState2[0],
    setSelectedTab = _useDerivedState2[1];
  var selectedTabLabel = (_tabs$find = tabs.find(function (tab) {
    return tab.value === selectedTab;
  })) === null || _tabs$find === undefined ? undefined : _tabs$find.label;
  var handleTabChange = useCallback(function (event, value) {
    setSelectedTab(value);
    handle(onChange)(event, value);
  }, [onChange, setSelectedTab]);
  var showHamburgerMenu = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")],
  // media queries
  [true],
  // values corresponding to media query
  false // default value
  );
  return variant === HEADER_VARIANT.SUB ? /*#__PURE__*/jsx(HeaderButton, {
    label: selectedTabLabel,
    "data-testid": "sector-menu-toggle",
    children: tabs.map(function (_ref2) {
      var as = _ref2.as,
        id = _ref2.id,
        label = _ref2.label,
        value = _ref2.value,
        to = _ref2.to,
        isExternal = _ref2.isExternal,
        tabRest = _objectWithoutProperties(_ref2, _excluded);
      var safeProps = safeRest(tabRest);
      var tabProps = getTabProps({
        component: as,
        to: to,
        isExternal: isExternal
      });
      var isSelected = value === selectedTab;
      return /*#__PURE__*/createElement(MenuItem, _objectSpread(_objectSpread(_objectSpread({}, tabProps), safeProps), {}, {
        as: as,
        id: id,
        label: label,
        key: value,
        isSelected: isSelected,
        onClick: function onClick(event) {
          return handleTabChange(event, value);
        }
      }));
    })
  }) : /*#__PURE__*/jsx(TabList, {
    theme: theme,
    role: "tablist",
    children: tabs.map(function (_ref3) {
      var as = _ref3.as,
        id = _ref3.id,
        label = _ref3.label,
        value = _ref3.value,
        to = _ref3.to,
        isExternal = _ref3.isExternal,
        tabRest = _objectWithoutProperties(_ref3, _excluded2);
      var component;
      if (showHamburgerMenu) {
        component = 'button';
      } else if (as) {
        component = as;
      } else if (to) {
        component = 'a';
      } else {
        component = 'button';
      }
      var safeProps = safeRest(tabRest);
      var tabProps = getTabProps({
        component: component,
        to: to,
        isExternal: isExternal
      });
      var isSelected = value === selectedTab;
      return /*#__PURE__*/jsx(TabItem, {
        id: id,
        role: "none",
        variant: theme.sideNav.mobile.tab.variant,
        children: /*#__PURE__*/jsx(TabComponent, _objectSpread(_objectSpread(_objectSpread({}, tabProps), safeProps), {}, {
          as: component,
          role: "tab",
          theme: theme,
          "aria-selected": isSelected,
          "data-variant": theme.sideNav.mobile.tab.variant,
          variant: theme.sideNav.mobile.tab.variant,
          onClick: function onClick(event) {
            return handleTabChange(event, value);
          },
          $isSelected: isSelected,
          children: label
        }))
      }, id);
    })
  });
};
process.env.NODE_ENV !== "production" ? HeaderTabs.propTypes = {
  /** Tabs to be displayed in the header on desktops */
  tabs: PropTypes.arrayOf(PropTypes.shape({
    /** component to use ('a', 'button', or Link from react-router-dom) */
    as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    /** id for the DOM element of the tab */
    id: PropTypes.string.isRequired,
    /** label displayed on the tab */
    label: PropTypes.node.isRequired,
    /** value used to determine selectedTab */
    value: PropTypes.string.isRequired,
    /** url (by default maps to href) or url/object/function (when using Link from react-router-dom) */
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),
    /** flag to indicate an external link that will open in a new tab */
    isExternal: PropTypes.bool
  })),
  /** value of currently selected tab */
  selectedTab: PropTypes.string,
  /** onChange handler for tabs */
  onChange: PropTypes.func
} : undefined;
process.env.NODE_ENV !== "production" ? HeaderTabs.displayName = 'HeaderTabs' : undefined;

export { HeaderTabs as default };
